import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { formatNumber, formatSerialNumber, getRiderLevelInfo } from '../../services/legendApi';
import SaluteButton from './SaluteButton';
import RiderBadge from '../rider/RiderBadge';
import styles from './LegendCard.module.css';

/**
 * 传奇卡片组件 - 展示铸造的传奇作品
 *
 * 设计理念：
 * - 金属镀铬边框，模拟哈雷工艺
 * - 深色背景，哈雷暗黑美学
 * - 荣耀数据醒目展示
 * - 骑士等级和徽章
 */
export default function LegendCard({
    legend,
    variant = 'default', // default | compact | featured
    showRider = true,
    showStats = true,
    linkable = true,
    className = '',
}) {
    const { language, getLocaleCode, t } = useLanguage();

    if (!legend) return null;

    const {
        id,
        serial_number,
        title,
        hero_image_url,
        config_summary,
        forged_at,
        is_featured,
        is_hall_of_fame,
        glory = {},
        rider = {},
    } = legend;

    const {
        salute_count = 0,
        view_count = 0,
        favorite_count = 0,
        share_count = 0,
        rank_percentile,
        is_saluted = false,
    } = glory;

    const levelInfo = getRiderLevelInfo(rider.level || 1, language);

    // 格式化日期
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString(getLocaleCode(), {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).replace(/\//g, '.');
    };

    // 卡片内容
    const cardContent = (
        <>
            {/* 顶部装饰 */}
            <div className={styles.topDecor}>
                {is_featured && (
                    <span className={styles.featuredBadge}>
                        📌 {{ zh: '编辑精选', en: "Editor's Pick", de: 'Redaktionsauswahl' }[language] || "Editor's Pick"}
                    </span>
                )}
                {is_hall_of_fame && (
                    <span className={styles.hallOfFameBadge}>
                        🏛️ {{ zh: '名人堂', en: 'Hall of Fame', de: 'Ruhmeshalle' }[language] || 'Hall of Fame'}
                    </span>
                )}
            </div>

            {/* 标题区域 */}
            <div className={styles.titleArea}>
                <h3 className={styles.title}>
                    <span className={styles.titleIcon}>🔥</span>
                    {title}
                    <span className={styles.titleIcon}>🔥</span>
                </h3>
                <div className={styles.divider} />
            </div>

            {/* 主图区域 */}
            <div className={styles.imageArea}>
                {hero_image_url ? (
                    <img
                        src={hero_image_url}
                        alt={title}
                        className={styles.heroImage}
                        loading="lazy"
                    />
                ) : (
                    <div className={styles.imagePlaceholder}>
                        <span>🏍️</span>
                    </div>
                )}
            </div>

            {/* 配置摘要 */}
            {config_summary && variant !== 'compact' && (
                <div className={styles.configSummary}>
                    <span className={styles.configIcon}>⚙️</span>
                    <span className={styles.configText}>
                        {Array.isArray(config_summary.highlights)
                            ? config_summary.highlights.join(' · ')
                            : `${config_summary.color || ''} · ${config_summary.wheels || ''}`
                        }
                    </span>
                </div>
            )}

            {/* 骑士信息 */}
            {showRider && (
                <div className={styles.riderArea}>
                    <div className={styles.riderInfo}>
                        <div className={styles.riderAvatar}>
                            {rider.avatar ? (
                                <img src={rider.avatar} alt={rider.nickname} />
                            ) : (
                                <span>👤</span>
                            )}
                            {rider.badges?.length > 0 && (
                                <div className={styles.riderBadges}>
                                    {rider.badges.slice(0, 2).map(badge => (
                                        <RiderBadge key={badge} badgeId={badge} size="small" />
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className={styles.riderMeta}>
                            <span className={styles.riderName}>@{rider.nickname || 'Unknown'}</span>
                            <span className={styles.riderLevel}>
                                {levelInfo.icon} {levelInfo.title} Lv.{rider.level || 1}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* 荣耀数据 */}
            {showStats && (
                <div className={styles.gloryStats}>
                    <div className={styles.statItem}>
                        <SaluteButton
                            legendId={id}
                            initialCount={salute_count}
                            initialSaluted={is_saluted}
                            size="small"
                            showCount={true}
                        />
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statIcon}>👁️</span>
                        <span className={styles.statValue}>{formatNumber(view_count)}</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statIcon}>⭐</span>
                        <span className={styles.statValue}>{formatNumber(favorite_count)}</span>
                    </div>
                    {variant !== 'compact' && (
                        <div className={styles.statItem}>
                            <span className={styles.statIcon}>🔄</span>
                            <span className={styles.statValue}>{formatNumber(share_count)}</span>
                        </div>
                    )}
                </div>
            )}

            {/* 底部信息 */}
            <div className={styles.footer}>
                <div className={styles.footerDivider} />
                <div className={styles.footerContent}>
                    {rank_percentile && rank_percentile <= 10 && (
                        <span className={styles.rankBadge}>
                            🏆 TOP {rank_percentile}%
                        </span>
                    )}
                    <span className={styles.serialNumber}>
                        {formatSerialNumber(serial_number || id)}
                    </span>
                    <span className={styles.forgedAt}>
                        ⏰ {formatDate(forged_at)} {{ zh: '铸造', en: 'Forged', de: 'Geschmiedet' }[language] || 'Forged'}
                    </span>
                </div>
            </div>
        </>
    );

    // 根据 variant 添加不同的类名
    const variantClass = styles[variant] || '';

    // 根据 linkable 决定是否包装为链接
    if (linkable) {
        return (
            <Link
                to={`/legend/${id}`}
                className={`${styles.legendCard} ${variantClass} ${className}`}
            >
                {cardContent}
            </Link>
        );
    }

    return (
        <div className={`${styles.legendCard} ${variantClass} ${className}`}>
            {cardContent}
        </div>
    );
}

/**
 * 传奇卡片骨架屏
 */
export function LegendCardSkeleton({ variant = 'default' }) {
    const variantClass = styles[variant] || '';

    return (
        <div className={`${styles.legendCard} ${styles.skeleton} ${variantClass}`}>
            <div className={styles.titleArea}>
                <div className={styles.skeletonTitle} />
            </div>
            <div className={styles.imageArea}>
                <div className={styles.skeletonImage} />
            </div>
            <div className={styles.riderArea}>
                <div className={styles.skeletonRider} />
            </div>
            <div className={styles.gloryStats}>
                <div className={styles.skeletonStat} />
                <div className={styles.skeletonStat} />
                <div className={styles.skeletonStat} />
            </div>
        </div>
    );
}
